import { saveAs } from 'file-saver';
import moment from 'moment';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatIncidentCode } from './formatCodes';

export const generateIncidentReport = async (incident, apiClient) => {
  try {
    // Verificar se o incidente tem os dados necessários
    if (!incident) {
      throw new Error('Dados do incidente não fornecidos');
    }
    
    // Gerar nome do arquivo de forma mais simples
    const incidentNumber = formatIncidentCode(incident.id);
    const orgName = incident.organization?.name || incident.org?.name || 'Organizacao';
    // Converter para camelCase: primeira letra de cada palavra em maiúscula e remover espaços
    const formattedOrgName = orgName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
    const date = moment().format('DD.MM.YYYY_HHmmss');
    const fileName = `${formattedOrgName}_Incidente_${incidentNumber}_${date}.pdf`;
    
    // Criar PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    // Configurações
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    
    let yPosition = margin;
    const gutter = 8;
    const columnWidth = (contentWidth - gutter) / 2;
    let currentColumn = 0; // 0 = esquerda, 1 = direita
    let columnX = margin;
    const switchToRightColumn = () => {
      currentColumn = 1;
      columnX = margin + columnWidth + gutter;
      yPosition = margin;
    };

    const ensureSpace = (requiredSpace = 6) => {
      const bottomLimit = pageHeight - margin;
      if (yPosition + requiredSpace > bottomLimit) {
        if (currentColumn === 0) {
          switchToRightColumn();
        } else {
          // Segunda coluna sem espaço: reduzir levemente o espaçamento para tentar caber
          // Mantemos tudo na primeira página conforme solicitado
          yPosition = bottomLimit - requiredSpace;
        }
      }
    };

    const writeTitle = (title) => {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      ensureSpace(8);
      pdf.text(title, columnX, yPosition);
      yPosition += 8;
    };

    const writeText = (text, fontSize = 9, lineHeight = 4) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', 'normal');
      const lines = pdf.splitTextToSize(String(text ?? ''), columnWidth);
      lines.forEach((line) => {
        ensureSpace(lineHeight);
        pdf.text(line, columnX, yPosition);
        yPosition += lineHeight;
      });
    };

    const writeBulletList = (items = [], fontSize = 9, lineHeight = 4) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', 'normal');
      items.forEach((item) => {
        const bullet = `• ${String(item)}`;
        const lines = pdf.splitTextToSize(bullet, columnWidth - 2);
        lines.forEach((line, idx) => {
          ensureSpace(lineHeight);
          const x = columnX + 2; // leve recuo para a bolinha
          pdf.text(line, x, yPosition);
          yPosition += lineHeight;
        });
      });
    };
    
    const writeKeyValueList = (pairs = [], fontSize = 9, lineHeight = 4) => {
      const items = pairs.map((p) => `${p.label} ${p.value}`);
      writeBulletList(items, fontSize, lineHeight);
    };
    
    // Cabeçalho com logo da organização
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(margin, yPosition - 5, contentWidth, 25, 2, 2, 'S');
    
    // Logo da organização (se disponível)
    const orgLogoUrl = incident.organization?.imageUrl || incident.org?.imageUrl;
    if (orgLogoUrl) {
      try {
        // Adicionar logo no canto esquerdo
        pdf.addImage(orgLogoUrl, 'PNG', margin + 8, yPosition, 18, 15);
      } catch (error) {
        console.warn('Erro ao carregar logo:', error);
      }
    }
    
    // Título do relatório
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('RELATÓRIO DE INCIDENTE LGPD', pageWidth / 2, yPosition + 8, { align: 'center' });
    
    // Nome da organização (usar dados do incidente se disponível)
    const displayOrgName = incident.organization?.name || incident.org?.name || 'Organização';
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(displayOrgName, pageWidth / 2, yPosition + 15, { align: 'center' });
    
    yPosition += 30;
    
    // Informações de geração
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Gerado em: ${moment().format('DD/MM/YYYY HH:mm:ss')}`, margin, yPosition);
    // Removida a paginação ao lado do cabeçalho e reduzido o espaçamento
    yPosition += 8;
    
    // Linha separadora
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 6;
    
    // INFORMAÇÕES DO INCIDENTE (Seção 1)
    writeTitle('INFORMAÇÕES DO INCIDENTE');
    
    // Dados básicos do incidente
    const incidentData = [
      { label: 'Número do Incidente:', value: formatIncidentCode(incident.id) },
      { label: 'Título:', value: String(incident.title || 'N/A') },
      { label: 'Data do Incidente:', value: incident.incidentDate ? moment(incident.incidentDate).format('DD/MM/YYYY HH:mm') : 'N/A' },
      { label: 'Data de Descoberta:', value: incident.discoveryDate ? moment(incident.discoveryDate).format('DD/MM/YYYY HH:mm') : 'N/A' },
      { label: 'Status:', value: getStatusText(incident.status) },
      { label: 'Tipo de Incidente:', value: getIncidentTypeText(incident.incidentType) },
      { label: 'Nível de Severidade:', value: getSeverityText(incident.severityLevel) },
      { label: 'Nº de Titulares Afetados:', value: String(incident.dataSubjectsCount || 0) },
    ];
    
    writeKeyValueList(incidentData, 9, 4);
    
    yPosition += 4;
    
    // DESCRIÇÃO DO INCIDENTE (Seção 2)
    if (incident.description) {
      writeTitle('DESCRIÇÃO DO INCIDENTE');
      writeBulletList([incident.description], 9, 4);
      yPosition += 6;
    }
    
    // CATEGORIAS DE DADOS AFETADAS (Seção 3)
    if (incident.dataCategories && incident.dataCategories.length > 0) {
      writeTitle('CATEGORIAS DE DADOS AFETADAS');
      writeBulletList(incident.dataCategories, 9, 4);
      yPosition += 6;
    }
    
    // SISTEMAS AFETADOS (Seção 4)
    if (incident.affectedSystems && incident.affectedSystems.length > 0) {
      writeTitle('SISTEMAS AFETADOS');
      writeBulletList(incident.affectedSystems, 9, 4);
      yPosition += 6;
    }
    
    // CAUSA RAIZ (Seção 5)
    if (incident.rootCause) {
      writeTitle('CAUSA RAIZ');
      writeBulletList([incident.rootCause], 9, 4);
      yPosition += 6;
    }
    
    // MEDIDAS TOMADAS (Seção 6)
    if (incident.immediateMeasures || incident.containmentMeasures || incident.recoveryMeasures || incident.preventiveMeasures) {
      writeTitle('MEDIDAS TOMADAS');
      const measures = [];
      if (incident.immediateMeasures) measures.push(`Medidas Imediatas: ${incident.immediateMeasures}`);
      if (incident.containmentMeasures) measures.push(`Medidas de Contenção: ${incident.containmentMeasures}`);
      if (incident.recoveryMeasures) measures.push(`Medidas de Recuperação: ${incident.recoveryMeasures}`);
      if (incident.preventiveMeasures) measures.push(`Medidas Preventivas: ${incident.preventiveMeasures}`);
      writeBulletList(measures, 9, 4);
      yPosition += 6;
    }
    
    // DPO RESPONSÁVEL (Seção 7)
    if (incident.dpo) {
      writeTitle('DPO RESPONSÁVEL');
      const dpoItems = [`Nome: ${incident.dpo.name}`];
      if (incident.dpo.email) dpoItems.push(`Email: ${incident.dpo.email}`);
      writeBulletList(dpoItems, 9, 4);
      yPosition += 4;
    }
    
    // Rodapé (igual ao Service Desk): linha, logo, data/hora e numeração
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      // Linha separadora do rodapé
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);

      // Logo da Solis no canto direito
      try {
        const solisLogoUrl = 'https://oxaxpvpxjaojruodsvbq.supabase.co/storage/v1/object/sign/adaptep/logo_solis_invert.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV85NDA2YWQ3MC01ZDM2LTRjZWMtYWJjYy03OTVhOWZkYzMyNDkiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhZGFwdGVwL2xvZ29fc29saXNfaW52ZXJ0LnBuZyIsImlhdCI6MTc1NzUyMTg1NiwiZXhwIjoxNzg5MDU3ODU2fQ.9MI3cgXnovBontRRjAZdc6pGvmt5EK99x_jB6QtZXd0';
        pdf.addImage(solisLogoUrl, 'PNG', pageWidth - 60, pageHeight - 18, 50, 12);
      } catch (error) {
        // Fallback com texto
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('SOLIS', pageWidth - 30, pageHeight - 12, { align: 'right' });
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text('LGPD', pageWidth - 30, pageHeight - 8, { align: 'right' });
      }

      // Data e hora no canto esquerdo
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text(moment().format('DD/MM/YYYY HH:mm'), margin, pageHeight - 12);

      // Numeração de páginas
      pdf.text(`Página ${i} de ${totalPages}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
    }
    
    // Salvar o PDF (usando o mesmo método do service desk)
    pdf.save(fileName);
    
  } catch (error) {
    console.error('Erro ao gerar relatório de incidente:', error);
    throw error;
  }
};

// Funções auxiliares para formatação
const getStatusText = (status) => {
  const statusMap = {
    'ABERTO': 'Aberto',
    'EM_ANALISE': 'Em Análise',
    'CONCLUIDO': 'Concluído',
    'ARQUIVADO': 'Arquivado'
  };
  return String(statusMap[status] || status || 'N/A');
};

const getIncidentTypeText = (type) => {
  const typeMap = {
    'VAZAMENTO_DADOS': 'Vazamento de Dados',
    'ACESSO_NAO_AUTORIZADO': 'Acesso Não Autorizado',
    'PERDA_DADOS': 'Perda de Dados',
    'ALTERACAO_NAO_AUTORIZADA': 'Alteração Não Autorizada',
    'DESTRUICAO_DADOS': 'Destruição de Dados',
    'OUTROS': 'Outros'
  };
  return String(typeMap[type] || type || 'N/A');
};

const getSeverityText = (severity) => {
  const severityMap = {
    'BAIXA': 'Baixa',
    'MEDIA': 'Média',
    'ALTA': 'Alta',
    'CRITICA': 'Crítica'
  };
  return String(severityMap[severity] || severity || 'N/A');
};

// Função para obter informações da organização do incidente (seguindo padrão do service desk)
const getIncidentOrganizationInfo = (incident) => {
  if (!incident) {
    return { name: null, logoUrl: null };
  }
  
  // Tentar obter informações da organização de diferentes formas
  const org = incident.organization || incident.org;
  if (!org) {
    return { name: null, logoUrl: null };
  }
  
  return {
    name: org.name,
    logoUrl: org.imageUrl
  };
};
